import * as React from 'react';
import clsx from 'clsx';

import {
  StyleRules,
  createStyles,
  WithStyles,
  withStyles,
  Button,
  Dialog,
  DialogProps,
  DialogActions,
  DialogContent,
  DialogActionsProps,
  DialogContentProps,
} from '@material-ui/core';
import { DIALOG_WIDTH, DIALOG_WIDTH_WIDER } from '../constants/dimensions';

export interface ModalDialogProps extends DialogProps {
  DialogComponent?: React.ComponentType<DialogProps>;
  DialogActionsComponent?: React.ComponentType<DialogActionsProps>;
  DialogContentComponent?: React.ComponentType<DialogContentProps>;
  onAccept: () => void;
  onDismiss: () => void;
  onClear: () => void;
  onSetToday: () => void;
  okLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  clearLabel?: React.ReactNode;
  todayLabel?: React.ReactNode;
  clearable?: boolean;
  showTodayButton?: boolean;
  showTabs?: boolean;
  wider?: boolean;
}

export const ModalDialog: React.FC<ModalDialogProps & WithStyles<typeof styles>> = ({
  children,
  classes,
  onAccept,
  onDismiss,
  onClear,
  onSetToday,
  okLabel,
  cancelLabel,
  clearLabel,
  todayLabel,
  clearable,
  showTodayButton,
  showTabs,
  wider,
  DialogComponent,
  DialogActionsComponent,
  DialogContentComponent,
  ...other
}) => {
  const DefaultDialog = DialogComponent || Dialog;
  const DefaultDialogContent = DialogContentComponent || DialogContent;
  const DefaultDialogActions = DialogActionsComponent || DialogActions;
  return (
    <DefaultDialog
      role="dialog"
      onClose={onDismiss}
      classes={{
        paper: clsx(classes.dialogRoot, {
          [classes.dialogRootWider]: wider,
        }),
      }}
      {...other}
    >
      <DefaultDialogContent children={children} className={classes.dialog} />

      <DefaultDialogActions
        classes={{
          root: clsx({
            [classes.withAdditionalAction]: clearable || showTodayButton,
          }),
        }}
      >
        {clearable && (
          <Button color="primary" onClick={onClear}>
            {clearLabel}
          </Button>
        )}

        {showTodayButton && (
          <Button color="primary" onClick={onSetToday}>
            {todayLabel}
          </Button>
        )}

        {cancelLabel && (
          <Button color="primary" variant="outlined" onClick={onDismiss}>
            {cancelLabel}
          </Button>
        )}

        {okLabel && (
          <Button color="primary" variant="contained" onClick={onAccept}>
            {okLabel}
          </Button>
        )}
      </DefaultDialogActions>
    </DefaultDialog>
  );
};

ModalDialog.displayName = 'ModalDialog';

export const styles: StyleRules = createStyles({
  dialogRoot: {
    minWidth: DIALOG_WIDTH,
    maxWidth: DIALOG_WIDTH,
  },
  dialogRootWider: {
    minWidth: DIALOG_WIDTH_WIDER,
  },
  dialog: {
    '&:first-child': {
      padding: 0,
    },
  },
  withAdditionalAction: {
    // set justifyContent to default value to fix IE11 layout bug
    // see https://github.com/dmtrKovalenko/material-ui-pickers/pull/267
    justifyContent: 'flex-start',

    '& > *:first-child': {
      marginRight: 'auto',
    },
  },
});

export default withStyles(styles, { name: 'MuiPickersModal' })(ModalDialog);
